import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/auth/AuthContext";
import { cn } from "@/lib/utils";
import { Loader2, Trash2, ArrowLeft, RotateCcw, ShieldAlert, UserPlus, X, Check, Database, Layers, Calendar, User as UserIcon, GitCompare } from "lucide-react";
import { toast } from "sonner";
import MdqCompare from "./MdqCompare";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MdqHistory({ onBack, onOpenRun }) {
  const { user } = useAuth();
  const [tab, setTab] = useState("history");
  const isAdmin = user?.role === "admin";
  const [runs, setRuns] = useState(null);
  const [dbStats, setDbStats] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/mdq/runs?limit=50`);
      setRuns(data.runs || []);
    } catch { toast.error("Failed to load history"); }
    if (isAdmin) {
      try { const { data } = await axios.get(`${API}/admin/db-stats`); setDbStats(data); } catch {}
    }
  }, [isAdmin]);

  useEffect(() => { refresh(); }, [refresh]);

  const remove = async (id) => {
    if (!confirm("Delete this analysis?")) return;
    try { await axios.delete(`${API}/mdq/runs/${id}`); toast.success("Deleted"); refresh(); }
    catch { toast.error("Delete failed"); }
  };

  const openOne = async (id) => {
    try { const { data } = await axios.get(`${API}/mdq/runs/${id}`); onOpenRun?.(data); }
    catch { toast.error("Failed to load"); }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-valantic-muted mb-1">
            {isAdmin ? "Admin · all analyses" : "Your analyses"}
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-valantic-black tracking-tight">Analysis vault</h2>
          <p className="text-sm text-valantic-muted mt-1">
            Persisted in MongoDB · {runs ? `${runs.length} record${runs.length === 1 ? "" : "s"}` : "loading…"}
          </p>
        </div>
        <button onClick={onBack} className="inline-flex items-center gap-2 h-9 px-3 bg-valantic-black text-white rounded-sm text-xs font-medium hover:bg-slate-800">
          <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.75} /> New analysis
        </button>
      </div>

      {/* Tab bar — Compare is available to all users; Admin tab is admin-only */}
      <div className="border-b border-valantic-border mb-4 flex gap-0">
        {[
          { id: "history", label: "History" },
          { id: "compare", label: "Compare runs", Icon: GitCompare },
          ...(isAdmin ? [{ id: "admin", label: "Admin & users" }] : []),
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={cn(
              "px-3 py-2.5 text-xs sm:text-sm transition-colors duration-150 border-b-2 inline-flex items-center gap-1.5",
              tab === t.id ? "text-valantic-black border-valantic-black font-medium" : "text-valantic-muted border-transparent hover:text-valantic-black"
            )}
          >
            {t.Icon && <t.Icon className="w-3.5 h-3.5" strokeWidth={1.75} />}
            {t.label}
          </button>
        ))}
      </div>

      {tab === "history" && <HistoryTable runs={runs} openOne={openOne} remove={remove} />}
      {tab === "compare" && <MdqCompare />}
      {tab === "admin" && isAdmin && <AdminPanel dbStats={dbStats} />}
    </div>
  );
}

function HistoryTable({ runs, openOne, remove }) {
  if (runs === null) {
    return (
      <div className="bg-white border border-valantic-border rounded-md p-10 flex items-center justify-center text-valantic-muted gap-2 text-sm">
        <Loader2 className="w-4 h-4 ibp-spin-slow" /> Loading…
      </div>
    );
  }
  if (runs.length === 0) {
    return (
      <div className="bg-white border border-valantic-border rounded-md p-10 text-center">
        <Database className="w-8 h-8 text-slate-400 mx-auto mb-3" strokeWidth={1.5} />
        <div className="font-display text-base font-medium text-valantic-black">No analyses yet</div>
        <p className="text-sm text-valantic-muted mt-1">Run your first MDQ analysis to populate the vault.</p>
      </div>
    );
  }
  return (
    <div className="bg-white border border-valantic-border rounded-md overflow-hidden">
      <div className="overflow-x-auto ibp-scroll">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-valantic-bg border-b border-valantic-border">
              {["Score", "System", "Industry", "Owner", "Created", ""].map(h => (
                <th key={h} className="text-left px-4 py-2 text-[10px] uppercase tracking-widest text-valantic-muted font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {runs.map(r => {
              const score = r?.summary?.overallScore;
              return (
                <tr key={r.id} className="border-b border-valantic-border last:border-0 hover:bg-slate-50/60" data-testid={`history-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <span className={cn(
                      "w-9 h-9 rounded-sm inline-flex items-center justify-center font-display font-bold text-sm",
                      score == null ? "bg-slate-100 text-slate-400" :
                      score < 30 ? "bg-rose-50 text-rose-700" :
                      score < 70 ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"
                    )}>{score ?? "?"}</span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-valantic-black">{r.product}</td>
                  <td className="px-4 py-3 text-xs">{r.industry}</td>
                  <td className="px-4 py-3 text-xs text-slate-700 truncate max-w-[180px]">{r.user_email || "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-600 font-mono whitespace-nowrap">{formatDate(r.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => openOne(r.id)} data-testid={`open-${r.id}`} className="text-xs h-7 px-2 border border-valantic-border bg-white rounded-sm hover:bg-slate-50">Open</button>
                      <button onClick={() => remove(r.id)} data-testid={`delete-${r.id}`} className="text-xs h-7 w-7 inline-flex items-center justify-center border border-valantic-border bg-white rounded-sm hover:bg-rose-50 hover:border-rose-200 hover:text-rose-700 text-valantic-muted">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminPanel({ dbStats }) {
  const [users, setUsers] = useState(null);
  const [audit, setAudit] = useState(null);
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [u, a] = await Promise.all([axios.get(`${API}/auth/users`), axios.get(`${API}/auth/audit-log`)]);
      setUsers(u.data.users || []);
      setAudit(a.data.events || []);
    } catch { toast.error("Failed to load admin data"); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const removeUser = async (id, email) => {
    if (!confirm(`Delete user ${email}?`)) return;
    try { await axios.delete(`${API}/auth/users/${id}`); toast.success("User deleted"); refresh(); }
    catch (e) { toast.error("Delete failed", { description: e?.response?.data?.detail }); }
  };

  return (
    <div className="space-y-5">
      {dbStats && (
        <div className="bg-white border border-valantic-border rounded-md p-5 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-0.5 val-gradient-warm" />
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-valantic-muted">Database & system</div>
              <div className="font-display text-base font-bold text-valantic-black">
                {dbStats.meta?.app_name || "Valantic MDQ Analyzer"}
                <span className="font-mono font-normal text-valantic-muted ml-2 text-xs">v{dbStats.meta?.app_version || "—"}</span>
              </div>
            </div>
            <span className={cn(
              "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-[10px] font-medium uppercase tracking-wider border",
              dbStats.ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"
            )}>
              <span className={cn("w-1.5 h-1.5 rounded-full", dbStats.ok ? "bg-emerald-500 ibp-pulse" : "bg-rose-500")} />
              {dbStats.ok ? "Connected" : "Unreachable"}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Field2 label="Database" value={dbStats.database} mono />
            <Field2 label="MongoDB" value={dbStats.mongo_version} mono />
            <Field2 label="First seeded" value={formatDate(dbStats.meta?.first_seeded_at)} />
            <Field2 label="Last boot" value={formatDate(dbStats.meta?.last_boot_at)} />
          </div>
          <div className="border border-valantic-border rounded-sm overflow-hidden">
            <div className="bg-valantic-bg border-b border-valantic-border px-3 py-2 grid grid-cols-12 gap-2 font-mono text-[9px] uppercase tracking-wider text-valantic-muted">
              <div className="col-span-3">Collection</div>
              <div className="col-span-2 text-right">Documents</div>
              <div className="col-span-7">Indexes</div>
            </div>
            {(dbStats.collections || []).map(c => (
              <div key={c.name} className="px-3 py-2 grid grid-cols-12 gap-2 text-xs items-center border-b border-valantic-border last:border-0" data-testid={`db-coll-${c.name}`}>
                <div className="col-span-3 font-mono">{c.name}</div>
                <div className="col-span-2 text-right font-mono font-bold">{c.count}</div>
                <div className="col-span-7 flex flex-wrap gap-1">
                  {c.indexes.length === 0
                    ? <span className="text-valantic-muted text-[11px]">—</span>
                    : c.indexes.map(i => (<span key={i} className="font-mono text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded-sm">{i}</span>))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-valantic-border rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-valantic-border flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-valantic-muted">Users</div>
            <div className="font-display text-base font-bold text-valantic-black">Authorized accounts</div>
          </div>
          <button onClick={() => setCreating(true)} data-testid="new-user-btn" className="inline-flex items-center gap-2 h-8 px-3 bg-valantic-black text-white rounded-sm text-xs font-medium hover:bg-slate-800">
            <UserPlus className="w-3.5 h-3.5" strokeWidth={1.75} /> New user
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-valantic-bg border-b border-valantic-border">
                {["Email", "Name", "Role", "Entitlements", "Created", ""].map(h => (
                  <th key={h} className="text-left px-4 py-2 text-[10px] uppercase tracking-widest text-valantic-muted font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(users || []).map(u => (
                <tr key={u.id} className="border-b border-valantic-border" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3 font-mono text-xs text-valantic-black">{u.email}</td>
                  <td className="px-4 py-3 text-xs">{u.name}</td>
                  <td className="px-4 py-3">
                    <span className={cn("inline-flex items-center px-2 py-0.5 rounded-sm border font-mono text-[10px] uppercase tracking-wider",
                      u.role === "admin" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-blue-50 text-blue-700 border-blue-200")}>{u.role}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1 max-w-md">
                      {(u.entitlements || []).map(e => (
                        <span key={e} className="font-mono text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded-sm">{e}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px] text-valantic-muted whitespace-nowrap">{formatDate(u.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    {u.role !== "admin" && (
                      <button onClick={() => removeUser(u.id, u.email)} data-testid={`delete-user-${u.email}`} className="text-xs h-7 w-7 inline-flex items-center justify-center border border-valantic-border bg-white rounded-sm hover:bg-rose-50 hover:border-rose-200 hover:text-rose-700 text-valantic-muted">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-valantic-border rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-valantic-border">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-valantic-muted">Audit log</div>
          <div className="font-display text-base font-bold text-valantic-black">Recent admin events</div>
        </div>
        <div className="divide-y divide-valantic-border">
          {(audit || []).slice(0, 30).map((ev, i) => (
            <div key={ev.id || i} className="px-5 py-3 flex items-center gap-4 text-xs">
              <ShieldAlert className="w-3.5 h-3.5 text-valantic-muted shrink-0" strokeWidth={1.5} />
              <div className="font-mono text-[10px] text-valantic-muted shrink-0 w-44">{formatDate(ev.ts)}</div>
              <div className="font-mono text-valantic-black shrink-0 w-40">{ev.event}</div>
              <div className="text-slate-700 truncate">
                <span className="text-valantic-muted">by</span> {ev.actor_email}
                {ev.target && (<><span className="text-valantic-muted ml-2">→</span> <span className="font-mono">{ev.target}</span></>)}
              </div>
            </div>
          ))}
          {audit && audit.length === 0 && <div className="px-5 py-6 text-xs text-valantic-muted text-center">No audit events yet.</div>}
        </div>
      </div>

      {creating && <NewUserModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); refresh(); }} />}
    </div>
  );
}

function NewUserModal({ onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await axios.post(`${API}/auth/users`, { email: email.trim().toLowerCase(), password, name: name.trim() });
      toast.success(`User created: ${email}`);
      onCreated();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : (Array.isArray(d) ? d.map(x => x?.msg || JSON.stringify(x)).join(" ") : e.message);
      setErr(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white border border-valantic-border rounded-md w-full max-w-md p-6 relative" data-testid="new-user-modal">
        <button onClick={onClose} className="absolute top-3 right-3 w-7 h-7 inline-flex items-center justify-center hover:bg-slate-100 rounded-sm"><X className="w-4 h-4" /></button>
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-valantic-muted mb-1">Admin · Users</div>
        <h3 className="font-display text-lg font-bold text-valantic-black">Add a new user</h3>
        <p className="text-xs text-valantic-muted mt-1 mb-5">User role with own-analysis access (<code className="font-mono">mdq:analyze</code>, <code className="font-mono">mdq:read_own</code>).</p>
        <form onSubmit={submit} className="space-y-3">
          <Lbl label="Email"><input data-testid="new-user-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full h-9 px-3 text-sm font-mono bg-white border border-valantic-border rounded-sm focus:outline-none focus:ring-2 focus:ring-valantic-black" /></Lbl>
          <Lbl label="Name"><input data-testid="new-user-name" value={name} onChange={e => setName(e.target.value)} placeholder="Jane Smith" className="w-full h-9 px-3 text-sm bg-white border border-valantic-border rounded-sm focus:outline-none focus:ring-2 focus:ring-valantic-black" /></Lbl>
          <Lbl label="Password" hint="min 8 chars"><input data-testid="new-user-password" type="password" value={password} onChange={e => setPassword(e.target.value)} minLength={8} required className="w-full h-9 px-3 text-sm font-mono bg-white border border-valantic-border rounded-sm focus:outline-none focus:ring-2 focus:ring-valantic-black" /></Lbl>
          {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-sm px-3 py-2 font-mono break-words">{err}</div>}
          <div className="flex gap-2 pt-2">
            <button type="submit" data-testid="new-user-submit" disabled={busy || !email || password.length < 8}
              className={cn("inline-flex items-center gap-2 h-9 px-4 rounded-sm text-xs font-medium",
                busy || !email || password.length < 8 ? "bg-slate-300 text-slate-500" : "bg-valantic-black text-white hover:bg-slate-800")}>
              {busy ? <Loader2 className="w-3.5 h-3.5 ibp-spin-slow" /> : <Check className="w-3.5 h-3.5" />} Create user
            </button>
            <button type="button" onClick={onClose} className="h-9 px-4 text-xs border border-valantic-border bg-white rounded-sm hover:bg-slate-50">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const Lbl = ({ label, hint, children }) => (
  <div>
    <div className="flex justify-between items-baseline mb-1.5">
      <label className="text-[10px] uppercase tracking-widest text-valantic-muted font-medium">{label}</label>
      {hint && <span className="text-[10px] text-valantic-muted">{hint}</span>}
    </div>
    {children}
  </div>
);
const Field2 = ({ label, value, mono }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-valantic-muted font-medium mb-0.5">{label}</div>
    <div className={cn("text-sm text-valantic-black", mono && "font-mono text-xs")}>{value || "—"}</div>
  </div>
);
function formatDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return s; }
}
